import React, { FC, useCallback, useState } from 'react';
import { Button, ButtonSize, ButtonTheme } from '../../shared/common/Button';
import { MATERIAL_ICON_CART } from '../../shared/common/Button/Icon';
import { createSelectionlist } from '../../store/orderlist/actions';
import { SelectionListDropDown } from '../../shared/common/SelectionListDropDown';
import { useDispatch } from 'react-redux';
import { useOrderLists } from '../../shared/common/useOrderLists';
import { PriceTier } from '../../../models/PriceTier';
import { BulkAddProductsToSelectionListResponse, BulkAddProductsToShoppingCartResponse } from '../../store/api';
import { ListActionTarget } from '../../../models/ListActionTarget';
import { useListContentChecks } from '../../shared/common/useListContentChecks';
import { Orderlist } from '../../../models/OrderList';
import { CheckBeforeAddingModal } from '../../shared/common/CheckBeforeAddingModal';
import { useTranslation } from 'react-i18next';
import { AddOptions } from '../../../models/ListContentCheckOptions';
import { UnavailableProductsModal } from '../../shared/common/UnavailableProductsModal';

type Props = {
    readonly onBulkAddToCart: (
        priceTier: PriceTier,
        performChecks: boolean
    ) => Promise<BulkAddProductsToShoppingCartResponse>;
    readonly onBulkAddToList: (
        listId: string,
        priceTier: PriceTier,
        performChecks: boolean
    ) => Promise<BulkAddProductsToSelectionListResponse>;
};

export const CatalogueMenuActionButtons: FC<Props> = ({ onBulkAddToCart, onBulkAddToList }) => {
    const dispatchRedux = useDispatch();
    const { orderlists } = useOrderLists();
    const [addingToCart, setAddingToCart] = useState(false);

    const onBulkAdd = useCallback(
        (
            performChecks: boolean,
            options: AddOptions
        ): Promise<BulkAddProductsToSelectionListResponse | BulkAddProductsToShoppingCartResponse> => {
            if (options.target === ListActionTarget.List) {
                return onBulkAddToList(options.list.id, options.priceTier!, performChecks);
            } else {
                return onBulkAddToCart(options.priceTier!, performChecks);
            }
        },
        [onBulkAddToList, onBulkAddToCart]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<AddOptions>({ onAction: onBulkAdd });

    const { t } = useTranslation();
    const handleBulkAddToList = useCallback(
        async (list: Orderlist) => {
            await changePrice({
                target: ListActionTarget.List,
                list,
                priceTier: { lendingModel: 'OCOU', level: 'MIN' }
            });
        },
        [changePrice]
    );

    const handleBulkAddToCart = useCallback(async () => {
        setAddingToCart(true);
        try {
            await changePrice({ target: ListActionTarget.Cart, priceTier: { lendingModel: 'OCOU', level: 'MIN' } });
        } finally {
            setAddingToCart(false);
        }
    }, [changePrice]);

    return (
        <>
            <Button
                text={t('addToCart')}
                icon={MATERIAL_ICON_CART}
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NEUTRAL}
                onClick={handleBulkAddToCart}
                disabled={addingToCart}
                isLoading={addingToCart}
            />
            <SelectionListDropDown
                label={t('addToList')}
                lists={orderlists}
                onCreateNewListClicked={newSelectionListName =>
                    dispatchRedux(createSelectionlist(newSelectionListName))
                }
                onListClick={handleBulkAddToList}
                disabled={false}
            />

            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </>
    );
};
